import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useReturns } from '../../contexts/ReturnsContext';
import { formatUKDate, formatUKDateTime, formatUKCurrency, formatUKTime } from '../../utils/formatters';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Chip,
  IconButton,
  Toolbar,
  Button,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Card
} from '@mui/material';
import AmazonUpload from './AmazonUpload';
import AmazonTrackingSearch from '../amazon/AmazonTrackingSearch';
import {
  Search as SearchIcon,
  GetApp as DownloadIcon,
  Add as AddIcon,
  Visibility as ViewIcon,
  FilterList as FilterIcon,
  KeyboardArrowDown,
  KeyboardArrowUp,
  CheckCircle as ReceiveIcon,
  Check as CheckIcon,
  Speed as CacheIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';

const ReturnsTable = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  
  // Channel name mapping
  const getChannelDisplayName = (channelSource) => {
    const channelMap = {
      'eBay': 'eBay Marketplace',
      'ebay': 'eBay Marketplace',
      'Amazon': 'Amazon UK',
      'amazon': 'Amazon UK',
      'Sellercloud': 'Sellercloud Direct',
      'sellercloud': 'Sellercloud Direct',
      'Website': 'XS Stock Website',
      'website': 'XS Stock Website',
      'Manual': 'Manual Entry',
      'manual': 'Manual Entry',
      'Wholesale': 'Wholesale Orders',
      'wholesale': 'Wholesale Orders',
      'B2B': 'Business Orders',
      'b2b': 'Business Orders'
    };
    
    return channelMap[channelSource] || channelSource || 'Unknown Channel';
  };
  
  // Use the cache context
  const {
    sellercloudsReturns,
    amazonReturns,
    loading,
    pagination,
    filters,
    fetchReturns,
    searchAllReturns,
    updateReturnStatus,
    deleteReturn,
    setPagination,
    setFilters
  } = useReturns();

  // Local UI state
  const [expandedAmazonRows, setExpandedAmazonRows] = useState(new Set());
  const [expandedSellercloudRows, setExpandedSellercloudRows] = useState(new Set());
  const [expandedEbayRows, setExpandedEbayRows] = useState(new Set());
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0); // 0 = Sellercloud, 1 = Amazon, 2 = eBay
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState({ sellercloud: [], amazon: [], ebay: [], orders: [] });
  const [ebayReturns, setEbayReturns] = useState([]);
  const [ebayLastCacheTime, setEbayLastCacheTime] = useState(null);
  const [ebayLoadingFromCache, setEbayLoadingFromCache] = useState(true);
  const initialChannelsLoaded = useRef(false);
  
  // Amazon search type state
  const [amazonSearchType, setAmazonSearchType] = useState('auto'); // auto, order_id, rma_id, tracking_id, asin
  
  // New Return dialog state
  const [newReturnDialogOpen, setNewReturnDialogOpen] = useState(false);
  const [newReturnType, setNewReturnType] = useState('');

  // QA Check dialog state
  const [qaDialog, setQaDialog] = useState(false);
  const [qaCheckNotes, setQaCheckNotes] = useState('');
  const [selectedReturnItem, setSelectedReturnItem] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [newReturnLoading, setNewReturnLoading] = useState(false);
  const [quickReturnData, setQuickReturnData] = useState({
    orderId: '',
    platform: 'sellercloud',
    customerEmail: '',
    customerName: '',
    reason: '',
    notes: ''
  });
  
  // Extract filters first
  const { search, statusFilter } = filters;
  
  // Derived state from context
  const returns = isSearching && search ? searchResults.sellercloud : sellercloudsReturns;
  const amazonDisplayReturns = isSearching && search ? (searchResults.amazon || []) : (amazonReturns || []);
  const ebayDisplayReturns = isSearching && search ? (searchResults.ebay || []) : (ebayReturns || []);
  const currentPlatform = activeTab === 0 ? 'sellercloud' : (activeTab === 1 ? 'amazon' : 'ebay');
  const isLoading = loading[currentPlatform];
  const currentPagination = pagination[currentPlatform];

  const toggleAmazonExpansion = (returnId) => {
    const newExpanded = new Set(expandedAmazonRows);
    if (newExpanded.has(returnId)) {
      newExpanded.delete(returnId);
    } else {
      newExpanded.add(returnId);
    }
    setExpandedAmazonRows(newExpanded);
  };

  const toggleSellercloudExpansion = (returnId) => {
    const newExpanded = new Set(expandedSellercloudRows);
    if (newExpanded.has(returnId)) {
      newExpanded.delete(returnId);
    } else {
      newExpanded.add(returnId);
    }
    setExpandedSellercloudRows(newExpanded);
  };

  const toggleEbayExpansion = (returnId) => {
    const newExpanded = new Set(expandedEbayRows);
    if (newExpanded.has(returnId)) {
      newExpanded.delete(returnId);
    } else {
      newExpanded.add(returnId);
    }
    setExpandedEbayRows(newExpanded);
  };

  const isAmazonReturn = (returnItem) => {
    return returnItem.platform === 'Amazon Upload' || 
           returnItem.platform === 'Amazon SP-API' || 
           returnItem.platform === 'Amazon';
  };

  const renderSellercloudDetails = (returnItem) => {
    return (
      <Collapse in={expandedSellercloudRows.has(returnItem.id)} timeout="auto" unmountOnExit>
        <Box sx={{ p: 3, bgcolor: '#f0f8ff', borderTop: '1px solid #82c8ff' }}>
          <Typography variant="h6" sx={{ mb: 2, color: '#0066cc', fontWeight: 600 }}>
            📋 Sellercloud Return Details
          </Typography>
          
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 2 }}>
            {/* Return Information */}
            <Paper sx={{ p: 2, bgcolor: 'white', border: '1px solid #82c8ff' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#0066cc' }}>Return Information</Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}><strong>RMA Number:</strong> {returnItem.return_number || 'N/A'}</Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}><strong>Platform:</strong> {returnItem.platform || 'N/A'}</Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}><strong>Return Date:</strong> {formatUKDate(returnItem.return_date)}</Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}><strong>Return Reason:</strong> {returnItem.return_reason || 'N/A'}</Typography>
              <Typography variant="body2"><strong>Status:</strong> {returnItem.return_status || 'N/A'}</Typography>
            </Paper>

            {/* Customer Information */}
            <Paper sx={{ p: 2, bgcolor: 'white', border: '1px solid #82c8ff' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#1976D2' }}>Customer Information</Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}><strong>Name:</strong> {returnItem.customer_name || 'N/A'}</Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}><strong>Email:</strong> {returnItem.customer_email || 'N/A'}</Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}><strong>Order ID:</strong> {returnItem.order_id || 'N/A'}</Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}><strong>Channel Order ID:</strong> {returnItem.channel_order_ref || 'N/A'}</Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}><strong>Channel:</strong> {getChannelDisplayName(returnItem.order_source)}</Typography>
              <Typography variant="body2"><strong>Tracking Number:</strong> {returnItem.tracking_number || 'N/A'}</Typography>
            </Paper>

            {/* Product Information */}
            <Paper sx={{ p: 2, bgcolor: 'white', border: '1px solid #82c8ff' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#E65100' }}>Product Information</Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}><strong>SKU:</strong> {(returnItem.items && returnItem.items[0]?.sku) || returnItem.sku || 'N/A'}</Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}><strong>Product Name:</strong> {(returnItem.items && returnItem.items[0]?.product_name) || returnItem.product_name || 'N/A'}</Typography>
              <Typography variant="body2"><strong>Quantity:</strong> {(returnItem.items && returnItem.items[0]?.quantity_to_return) || returnItem.quantity || 1}</Typography>
            </Paper>

            {/* Financial & Processing */}
            <Paper sx={{ p: 2, bgcolor: 'white', border: '1px solid #82c8ff' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#2E7D32' }}>Financial & Processing</Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}><strong>Refund Amount:</strong> {formatUKCurrency(returnItem.refund_amount)}</Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}><strong>Created:</strong> {formatUKDate(returnItem.created_at)}</Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}><strong>Updated:</strong> {formatUKDate(returnItem.updated_at)}</Typography>
              <Typography variant="body2"><strong>Internal ID:</strong> {returnItem.id || 'N/A'}</Typography>
            </Paper>

            {/* Notes & Additional Info */}
            {returnItem.notes && (
              <Paper sx={{ p: 2, bgcolor: 'white', border: '1px solid #82c8ff' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#9C27B0' }}>Notes & Comments</Typography>
                <Typography variant="body2" sx={{ 
                  whiteSpace: 'pre-wrap',
                  maxHeight: '100px',
                  overflow: 'auto',
                  bgcolor: '#f8f9fa',
                  p: 1,
                  borderRadius: 1,
                  border: '1px solid #dee2e6'
                }}>
                  {returnItem.notes}
                </Typography>
              </Paper>
            )}
          </Box>
        </Box>
      </Collapse>
    );
  };

  const renderAmazonDetails = (returnItem) => {
    if (!isAmazonReturn(returnItem)) return null;

    // Parse label details
    let labelDetails = {};
    try {
      labelDetails = typeof returnItem.label_details === 'string' 
        ? JSON.parse(returnItem.label_details) 
        : returnItem.label_details || {};
    } catch (e) {
      labelDetails = {};
    }

    return (
      <Collapse in={expandedAmazonRows.has(returnItem.id)} timeout="auto" unmountOnExit>
        <Box sx={{ p: 3, bgcolor: '#fff8f0', borderTop: '1px solid #FFE082' }}>
          <Typography variant="h6" sx={{ mb: 2, color: '#FF9900', fontWeight: 600 }}>
            📦 Amazon Return Details
          </Typography>
          
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 2 }}>
            {/* Product Info */}
            <Paper sx={{ p: 2, bgcolor: 'white', border: '1px solid #FFE082' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#E65100' }}>Product Info</Typography>
              <Typography variant="body2"><strong>ASIN:</strong> {returnItem.asin || 'N/A'}</Typography>
              <Typography variant="body2"><strong>SKU:</strong> {returnItem.sku || 'N/A'}</Typography>
              <Typography variant="body2"><strong>Product:</strong> {returnItem.product_name || 'N/A'}</Typography>
              <Typography variant="body2"><strong>Quantity:</strong> {returnItem.return_quantity || '1'}</Typography>
              <Typography variant="body2"><strong>Category:</strong> {returnItem.category || 'N/A'}</Typography>
            </Paper>

            {/* Order Info */}
            <Paper sx={{ p: 2, bgcolor: 'white', border: '1px solid #FFE082' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#1976D2' }}>Order Info</Typography>
              <Typography variant="body2"><strong>Order ID:</strong> {returnItem.order_id || 'N/A'}</Typography>
              <Typography variant="body2"><strong>Order Date:</strong> {formatUKDate(returnItem.orderDate)}</Typography>
              <Typography variant="body2"><strong>Order Amount:</strong> £{returnItem.orderAmount || '0.00'}</Typography>
              <Typography variant="body2"><strong>Prime:</strong> {returnItem.isPrime === 'Y' ? 'Yes' : 'No'}</Typography>
              <Typography variant="body2"><strong>A-to-Z Claim:</strong> {returnItem.aToZClaim === 'true' ? 'Yes' : 'No'}</Typography>
            </Paper>

            {/* Shipping Info */}
            <Paper sx={{ p: 2, bgcolor: 'white', border: '1px solid #FFE082' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#2E7D32' }}>Shipping Info</Typography>
              <Typography variant="body2"><strong>Tracking ID:</strong> {labelDetails.tracking_id || returnItem.tracking_number || 'N/A'}</Typography>
              <Typography variant="body2"><strong>Carrier:</strong> {returnItem.returnCarrier || 'N/A'}</Typography>
              <Typography variant="body2"><strong>Return Type:</strong> {returnItem.returnType || 'N/A'}</Typography>
              <Typography variant="body2"><strong>In Policy:</strong> {returnItem.inPolicy === 'Y' ? 'Yes' : 'No'}</Typography>
              <Typography variant="body2"><strong>Label Paid By:</strong> {returnItem.labelToBePaidBy || 'Customer'}</Typography>
            </Paper>

            {/* Financial Info */}
            <Paper sx={{ p: 2, bgcolor: 'white', border: '1px solid #FFE082' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#9C27B0' }}>Financial Info</Typography>
              <Typography variant="body2"><strong>Refund Amount:</strong> {formatUKCurrency(returnItem.refundAmount)}</Typography>
              <Typography variant="body2"><strong>Label Cost:</strong> {labelDetails.currency_code || 'GBP'} {labelDetails.label_cost || '0.00'}</Typography>
              <Typography variant="body2"><strong>Resolution:</strong> {returnItem.resolution || 'Pending'}</Typography>
              <Typography variant="body2"><strong>Status:</strong> {returnItem.return_status || 'Processing'}</Typography>
            </Paper>
          </Box>
        </Box>
      </Collapse>
    );
  };

  // eBay Returns Caching Functions
  const loadEbayCachedData = () => {
    try {
      const cached = localStorage.getItem('ebayReturnsCache');
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        const age = Date.now() - timestamp;
        const maxAge = 10 * 60 * 1000; // 10 minutes
        
        if (age < maxAge) {
          console.log(`🚀 Loading eBay returns from cache (${Math.round(age / 1000 / 60)} minutes old)`);
          setEbayReturns(data);
          setEbayLastCacheTime(timestamp);
          setEbayLoadingFromCache(true);
          return true;
        } else {
          console.log('🗑️ eBay cache expired, removing...');
          localStorage.removeItem('ebayReturnsCache');
        }
      }
    } catch (error) {
      console.error('❌ Error loading eBay cache:', error);
      localStorage.removeItem('ebayReturnsCache');
    }
    return false;
  };

  const saveEbayCache = (data) => {
    try {
      const cacheData = {
        data: data,
        timestamp: Date.now()
      };
      localStorage.setItem('ebayReturnsCache', JSON.stringify(cacheData));
      setEbayLastCacheTime(Date.now());
      console.log(`💾 eBay returns cached (${data.length} returns)`);
    } catch (error) {
      console.error('❌ Error saving eBay cache:', error);
    }
  };

  const fetchEbayReturns = async (silent = false) => {
    try {
      if (!silent) {
        console.log('🔍 Fetching eBay returns...');
      } else {
        console.log('🔄 Silently updating eBay returns in background...');
      }
      
      const startTime = performance.now();
      
      // Build query parameters including status filter
      const queryParams = new URLSearchParams({
        platform: 'ebay',
        limit: '250',
        fresh: 'true'
      });
      
      // Add status filter if set
      if (statusFilter && statusFilter !== '') {
        queryParams.set('status', statusFilter);
        console.log(`🎯 Applying status filter to eBay returns: ${statusFilter}`);
      }
      
      // Use direct eBay returns endpoint for better performance
      const sessionToken = localStorage.getItem('sessionToken');
      const response = await fetch(`/api/returns?${queryParams}`, {
        headers: sessionToken ? { Authorization: `Bearer ${sessionToken}` } : {}
      });
      const data = await response.json();
      
      const endTime = performance.now();
      console.log(`⏱️ eBay returns ${silent ? 'background update' : 'fetch'} took ${Math.round(endTime - startTime)}ms`);
      
      if (data.success) {
        const ebayData = data.data || [];
        setEbayReturns(ebayData);
        saveEbayCache(ebayData);
        setEbayLoadingFromCache(false);
        
        if (silent) {
          console.log(`🔄 Background update complete: ${ebayData.length} eBay returns`);
        } else {
          console.log(`✅ Loaded ${ebayData.length} eBay returns`);
        }
      }
    } catch (error) {
      console.error(`❌ Error ${silent ? 'updating' : 'fetching'} eBay returns:`, error);
      setEbayLoadingFromCache(false);
    }
  };

  const refreshEbayReturns = () => {
    console.log('🔄 Force refreshing eBay returns...');
    localStorage.removeItem('ebayReturnsCache');
    setEbayLoadingFromCache(false);
    fetchEbayReturns(false);
  };

  const refreshSellercloudReturns = () => {
    console.log('🔄 Force refreshing Sellercloud returns...');
    // Clear any cached data and force a fresh fetch
    fetchReturns('sellercloud', 0, 250, search, statusFilter, true);
  };

  useEffect(() => {
    // Populate every channel count and table on first entry to All Returns.
    if (!initialChannelsLoaded.current) {
      initialChannelsLoaded.current = true;
      fetchReturns('sellercloud', 0, 250, '', statusFilter, true);
      fetchReturns('amazon', 0, 250, '', statusFilter, true);
      fetchEbayReturns(false);
      return;
    }

    if (!search || search.trim() === '') {
      if (currentPlatform === 'ebay') {
        // If status filter is applied, always fetch fresh data (don't use cache)
        if (statusFilter && statusFilter !== '') {
          console.log(`🎯 Status filter applied (${statusFilter}), skipping cache for eBay returns`);
          fetchEbayReturns(false);
        } else {
          // Try to load from cache first for instant display when no status filter
          const cached = loadEbayCachedData();
          if (cached) {
            // Data loaded from cache, now silently update in background
            setTimeout(() => fetchEbayReturns(true), 100); // Small delay to avoid blocking UI
          } else {
            // No cache, fetch normally
            fetchEbayReturns(false);
          }
        }
      } else {
        fetchReturns(currentPlatform, 0, 250, '', statusFilter);
      }
    }
  }, [statusFilter, activeTab]);

  // Clean up unused location state
  useEffect(() => {
    if (location.state?.amazonTrackingResults) {
      // Clear the navigation state after processing to avoid stale references
      window.history.replaceState(null, '');
    }
  }, [location.state]);

  useEffect(() => {
    // Set up auto-refresh every 3 minutes for returns table
    const interval = setInterval(() => {
      console.log('🔄 Auto-refreshing returns table...');
      if (currentPlatform === 'ebay') {
        fetchEbayReturns(true); // Silent background update
      } else {
        fetchReturns(currentPlatform, 0, 250, search, statusFilter, true);
      }
    }, 3 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [search, statusFilter, activeTab]);

  const handleChangePage = (event, newPage) => {
    setPagination(currentPlatform, { page: newPage });
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setPagination(currentPlatform, { page: 0, rowsPerPage: newRowsPerPage });
  };

  const handleSearchChange = async (newSearch) => {
    setFilters({ search: newSearch });
    setPagination(currentPlatform, { page: 0 }); // Reset to first page when searching
    
    if (newSearch && newSearch.trim() !== '') {
      setIsSearching(true);
      try {
        console.log('🔍 Search Debug:', {
          currentPlatform,
          activeTab,
          amazonSearchType,
          newSearch: newSearch.trim(),
          willUseTargeted: currentPlatform === 'amazon' && amazonSearchType !== 'auto'
        });
        
        // Optimize search based on current tab to avoid unnecessary API calls
        if (currentPlatform === 'amazon') {
          // Only search Amazon when on Amazon tab
          console.log(`🎯 Fast Amazon-only search: ${amazonSearchType} for "${newSearch.trim()}"`);
          const amazonData = await fetchReturns('amazon', 0, 250, newSearch.trim(), statusFilter, true, amazonSearchType);
          setSearchResults({ 
            sellercloud: [], 
            amazon: amazonData || [], 
            ebay: [], 
            orders: [] 
          });
        } else if (currentPlatform === 'sellercloud') {
          // Only search Sellercloud when on Sellercloud tab  
          console.log(`🎯 Fast Sellercloud-only search for "${newSearch.trim()}"`);
          const sellercloudsData = await fetchReturns('sellercloud', 0, 100, newSearch.trim(), statusFilter, true);
          setSearchResults({ 
            sellercloud: sellercloudsData || [], 
            amazon: [], 
            ebay: [], 
            orders: [] 
          });
        } else if (currentPlatform === 'ebay') {
          // Only search eBay when on eBay tab
          console.log(`🎯 Fast eBay-only search for "${newSearch.trim()}"`);
          const ebayData = await fetchEbayReturns(true, newSearch.trim());
          setSearchResults({ 
            sellercloud: [], 
            amazon: [], 
            ebay: ebayData || [], 
            orders: [] 
          });
        } else {
          // Use unified search only when needed (e.g., "All" tab if it exists)
          console.log('📡 Using unified search for:', { currentPlatform, amazonSearchType });
          const results = await searchAllReturns(newSearch.trim(), statusFilter);
          setSearchResults(results);
        }
      } catch (error) {
        console.error('Error performing search:', error);
        setSearchResults({ sellercloud: [], amazon: [], ebay: [], orders: [] });
      }
    } else {
      // When search is empty, refresh all data to original view
      setIsSearching(false);
      setSearchResults({ sellercloud: [], amazon: [], ebay: [], orders: [] });
      
      // Refresh all platform data to show original view
      console.log('🔄 Search cleared - refreshing all platform data...');
      if (currentPlatform === 'ebay') {
        fetchEbayReturns(false);
      } else {
        fetchReturns(currentPlatform, 0, 100, '', statusFilter, true); // Force refresh
      }
      
      // Also refresh other platforms for complete data sync
      if (currentPlatform !== 'sellercloud') {
        fetchReturns('sellercloud', 0, 100, '', statusFilter, true);
      }
      if (currentPlatform !== 'amazon') {
        fetchReturns('amazon', 0, 100, '', statusFilter, true);
      }
      if (currentPlatform !== 'ebay') {
        fetchEbayReturns(false);
      }
    }
  };

  const handleStatusFilterChange = (newStatus) => {
    setFilters({ statusFilter: newStatus });
    setPagination(currentPlatform, { page: 0 }); // Reset to first page when filtering
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return { bgcolor: '#e8f5e8', color: '#2e7d32' };
      case 'pending':
        return { bgcolor: '#fff3e0', color: '#e65100' };
      case 'received':
        return { bgcolor: '#e8f5e8', color: '#2e7d32' }; // Changed from blue to green
      case 'processing':
        return { bgcolor: '#f3e5f5', color: '#7b1fa2' };
      case 'archived':
        return { bgcolor: '#f5f5f5', color: '#757575' };
      default:
        return { bgcolor: '#f5f5f5', color: '#666' };
    }
  };

  const handleUpdateReturnStatus = async (returnId, newStatus) => {
    try {
      await updateReturnStatus(currentPlatform, returnId, newStatus);
    } catch (error) {
      console.error('Error updating return status:', error);
    }
  };

  const handleDeleteReturn = async (returnId) => {
    if (!window.confirm('Are you sure you want to permanently delete this Amazon return? This action cannot be undone.')) {
      return;
    }
    
    try {
      const result = await deleteReturn(currentPlatform, returnId);
      if (!result.success) {
        alert(result.message || 'Failed to delete return');
      }
    } catch (error) {
      console.error('Error deleting return:', error);
      alert('Error deleting return');
    }
  };

  const handleReceiveReturn = (returnItem) => {
    setSelectedReturnItem(returnItem);
    setQaDialog(true);
  };

  const handleQACheckComplete = async () => {
    if (!selectedReturnItem) return;

    setIsUpdating(true);
    try {
      const sessionToken = localStorage.getItem('sessionToken');
      const response = await fetch(`/api/returns/receive/${selectedReturnItem.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionToken}`
        },
        body: JSON.stringify({
          quantity: selectedReturnItem.quantity || 1,
          notes: qaCheckNotes.trim() ? `QA Check: ${qaCheckNotes.trim()}` : 'QA Check completed - no issues noted'
        })
      });

      const result = await response.json();
      
      if (result.success) {
        alert('Return successfully marked as received in Sellercloud!');
        setQaDialog(false);
        setQaCheckNotes('');
        setSelectedReturnItem(null);
        // Refresh the returns data
        fetchReturns(currentPlatform, 0, 100, search, statusFilter, true);
      } else {
        alert(`Failed to receive return: ${result.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error receiving return:', error);
      alert('Error processing receive return request');
    } finally {
      setIsUpdating(false);
    }
  };

  // New Return handlers
  const handleNewReturnTypeSelect = (type) => {
    setNewReturnType(type);
    if (type === 'full') {
      navigate('/returns/new');
      setNewReturnDialogOpen(false);
    }
  };

  const handleQuickReturnSubmit = async () => {
    try {
      setNewReturnLoading(true);
      
      // Navigate to full create page with pre-filled data
      navigate('/returns/new', {
        state: {
          quickData: quickReturnData
        }
      });
      
      setNewReturnDialogOpen(false);
      setNewReturnType('');
      setQuickReturnData({
        orderId: '',
        platform: 'sellercloud',
        customerEmail: '',
        customerName: '',
        reason: '',
        notes: ''
      });
    } catch (error) {
      console.error('Error creating quick return:', error);
      alert('Error creating return');
    } finally {
      setNewReturnLoading(false);
    }
  };

  return (
    <Box sx={{ p: 1, maxWidth: '100%', overflow: 'hidden', height: '100vh' }}>
      <Paper sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Toolbar sx={{ pl: 2, pr: 1, bgcolor: '#f8f9fa', borderBottom: '1px solid #dee2e6' }}>
          <Typography variant="h6" sx={{ flex: '1 1 100%', fontWeight: 600, color: '#495057' }}>
            Returns Management
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flex: '0 0 auto' }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setNewReturnDialogOpen(true)}
              sx={{ bgcolor: '#28a745', '&:hover': { bgcolor: '#218838' } }}
            >
              New Return
            </Button>
            
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={() => setUploadDialogOpen(true)}
              sx={{ bgcolor: '#FF9900', '&:hover': { bgcolor: '#e6880a' } }}
            >
              Upload Amazon XML
            </Button>
            
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              sx={{ borderColor: '#6c757d', color: '#6c757d', '&:hover': { borderColor: '#545b62' } }}
            >
              Export
            </Button>
          </Box>
        </Toolbar>
        
        {/* Amazon returns search — accepts an order id or a tracking number */}
        <Box sx={{ p: 2, bgcolor: '#fffbf5', borderBottom: '2px solid #FF9900' }}>
          <AmazonTrackingSearch 
            compact={true}
            placeholder="🔍 Amazon order ID or tracking number — searches Seller Central returns"
            title="Amazon Returns Search"
            onSearch={(trackingNumber, type) => {
              // Optional: Also search local database when user searches Amazon
              if (type === 'amazon-direct') {
                console.log(`Amazon search for: ${trackingNumber}`);
              }
            }}
          />
        </Box>

        <Toolbar sx={{ pl: 2, pr: 1, minHeight: '48px !important', bgcolor: '#ffffff' }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', width: '100%' }}>
            {/* Amazon Search Type Dropdown - only show when Amazon tab is active */}
            {activeTab === 1 && (
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Search Type</InputLabel>
                <Select
                  value={amazonSearchType}
                  label="Search Type"
                  onChange={(e) => setAmazonSearchType(e.target.value)}
                >
                  <MenuItem value="auto">Auto Detect</MenuItem>
                  <MenuItem value="order_id">Order ID</MenuItem>
                  <MenuItem value="rma_id">RMA ID</MenuItem>
                  <MenuItem value="tracking_id">Tracking ID</MenuItem>
                  <MenuItem value="sku">SKU</MenuItem>
                </Select>
              </FormControl>
            )}
            
            <TextField
              size="small"
              placeholder={
                activeTab === 1 
                  ? amazonSearchType === 'auto' 
                    ? "Search Amazon returns (auto-detect type)..."
                    : amazonSearchType === 'order_id'
                    ? "Enter Amazon Order ID (e.g. 203-4129776-9787514)..."
                    : amazonSearchType === 'rma_id'
                    ? "Enter RMA ID (e.g. DX2CXWxYRRMA)..."
                    : amazonSearchType === 'tracking_id'
                    ? "Enter Tracking Number (e.g. QY454903999GB)..."
                    : "Enter SKU (e.g. WKDAC-9813)..."
                  : "Search by Order ID, Tracking ID, SKU, Name..."
              }
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 350, flex: 1 }}
            />
            
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => handleStatusFilterChange(e.target.value)}
                label="Status"
              >
                <MenuItem value="">All Statuses</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="received">Received</MenuItem>
                <MenuItem value="processing">Processing</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="archived">Archived</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Toolbar>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
            <Tab 
              label={`Sellercloud Returns (${returns.length})`} 
              sx={{ 
                textTransform: 'none', 
                fontWeight: activeTab === 0 ? 600 : 400,
                color: isSearching && search && returns.length > 0 ? '#28a745' : 'inherit'
              }}
            />
            <Tab 
              label={`Amazon Returns (${amazonDisplayReturns.length})`} 
              sx={{ 
                textTransform: 'none', 
                fontWeight: activeTab === 1 ? 600 : 400,
                color: isSearching && search && amazonDisplayReturns.length > 0 ? '#28a745' : 'inherit'
              }}
            />
            <Tab 
              label={`eBay Returns (${ebayDisplayReturns.length})`} 
              sx={{ 
                textTransform: 'none', 
                fontWeight: activeTab === 2 ? 600 : 400,
                color: isSearching && search && ebayDisplayReturns.length > 0 ? '#28a745' : 'inherit'
              }}
            />
          </Tabs>
          
          {/* eBay Cache Status Indicator */}
          {activeTab === 2 && !isSearching && ebayLastCacheTime && (
            <Box sx={{ p: 1, bgcolor: '#f0f8ff', borderTop: '1px solid #cce7ff', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CacheIcon sx={{ color: ebayLoadingFromCache ? '#1976d2' : '#666', fontSize: 16 }} />
                <Typography variant="body2" sx={{ color: '#1976d2', fontSize: '0.75rem' }}>
                  {ebayLoadingFromCache ? 
                    `⚡ Data from cache (${formatUKTime(ebayLastCacheTime)})` : 
                    `📡 Live data updated at ${formatUKTime(ebayLastCacheTime)}`}
                </Typography>
              </Box>
              <Button
                size="small"
                startIcon={<RefreshIcon />}
                onClick={refreshEbayReturns}
                sx={{ 
                  fontSize: '0.7rem', 
                  minWidth: 'auto',
                  px: 1,
                  py: 0.5,
                  textTransform: 'none'
                }}
              >
                Refresh
              </Button>
            </Box>
          )}
          
          {/* Search indicator */}
          {isSearching && search && (
            <Box sx={{ p: 1, bgcolor: '#e8f5e8', borderTop: '1px solid #c8e6c9' }}>
              <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 500, textAlign: 'center' }}>
                {currentPlatform === 'amazon' && amazonSearchType !== 'auto' ? (
                  `🎯 Targeted Amazon search (${amazonSearchType.replace('_', ' ').toUpperCase()}) for: "${search}"`
                ) : (
                  `🔍 Searching across all returns for: "${search}"`
                )}
                {returns.length + amazonDisplayReturns.length + ebayDisplayReturns.length > 0 && 
                  ` - Found ${returns.length + amazonDisplayReturns.length + ebayDisplayReturns.length} results`
                }
                {returns.length + amazonDisplayReturns.length + ebayDisplayReturns.length === 0 && searchResults.orders && searchResults.orders.length > 0 && 
                  ` - No returns found, but found ${searchResults.orders.length} order(s) available for RMA creation`
                }
              </Typography>
            </Box>
          )}
          
          {/* Order Results for RMA Creation - Show when no returns found but orders exist */}
          {isSearching && search && returns.length + amazonDisplayReturns.length + ebayDisplayReturns.length === 0 && searchResults.orders && searchResults.orders.length > 0 && (
            <Box sx={{ p: 2, bgcolor: '#fff3cd', borderTop: '1px solid #ffeaa7' }}>
              <Typography variant="h6" sx={{ color: '#856404', fontWeight: 600, mb: 2 }}>
                📋 Orders Available for RMA Creation
              </Typography>
              {searchResults.orders.map((order, index) => (
                <Paper key={index} elevation={1} sx={{ p: 2, mb: 2, bgcolor: '#ffffff', border: '1px solid #e0e0e0' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1976d2', mb: 1 }}>
                        Order: {order.orderId || order.OrderID || order.channelOrderId || order.AmazonOrderId}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                        Customer: {order.customerName || order.BuyerInfo?.BuyerEmail || 'N/A'}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                        Platform: {order.platform === 'amazon' ? 'Amazon' : 'Sellercloud'}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                        Total: {order.totalAmount || order.OrderTotal?.Amount || order.TotalInfo?.GrandTotal || 'N/A'} {order.currency || order.OrderTotal?.CurrencyCode || 'GBP'}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#666' }}>
                        Status: {order.status || order.OrderStatus || order.Statuses?.OrderStatus || 'N/A'}
                      </Typography>
                    </Box>
                    <Button
                      variant="contained"
                      size="small"
                      sx={{ 
                        bgcolor: '#28a745', 
                        '&:hover': { bgcolor: '#218838' },
                        minWidth: '120px'
                      }}
                      onClick={() => {
                        // Pre-fill the quick return form and open dialog
                        setQuickReturnData({
                          orderId: order.orderId || order.OrderID || order.channelOrderId || order.AmazonOrderId,
                          platform: order.platform || 'sellercloud',
                          customerEmail: order.customerEmail || order.BuyerInfo?.BuyerEmail || '',
                          customerName: order.customerName || '',
                          reason: '',
                          notes: ''
                        });
                        setNewReturnType('quick');
                        setNewReturnDialogOpen(true);
                      }}
                    >
                      Create RMA
                    </Button>
                  </Box>
                </Paper>
              ))}
            </Box>
          )}
        </Box>

        {/* Tab Content - Sellercloud Returns */}
        {activeTab === 0 && (
          <>
            {/* Sellercloud Refresh Controls */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1, bgcolor: '#f8f9fa', borderBottom: '1px solid #dee2e6' }}>
              <Typography variant="body2" color="text.secondary">
                Sellercloud Returns - Auto-refreshes every 3 minutes
              </Typography>
              <Button
                size="small"
                startIcon={<RefreshIcon />}
                onClick={refreshSellercloudReturns}
                sx={{ 
                  fontSize: '0.7rem', 
                  minWidth: 'auto',
                  px: 1,
                  py: 0.5,
                  textTransform: 'none',
                  bgcolor: '#007bff',
                  color: 'white',
                  '&:hover': { bgcolor: '#0056b3' }
                }}
              >
                Refresh Now
              </Button>
            </Box>
            <TableContainer sx={{ px: 0, width: '100%', height: 'calc(100vh - 320px)', overflow: 'auto' }}>
            <Table stickyHeader size="small" sx={{ width: '100%' }}>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                  <TableCell sx={{ fontWeight: 600, py: 1.5, px: 2, fontSize: '0.875rem', color: '#495057', borderBottom: '2px solid #dee2e6', width: '40px' }}></TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 1.5, px: 2, fontSize: '0.875rem', color: '#495057', borderBottom: '2px solid #dee2e6' }}>Return Number</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 1.5, px: 2, fontSize: '0.875rem', color: '#495057', borderBottom: '2px solid #dee2e6' }}>Customer</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 1.5, px: 2, fontSize: '0.875rem', color: '#495057', borderBottom: '2px solid #dee2e6' }}>Internal Order</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 1.5, px: 2, fontSize: '0.875rem', color: '#495057', borderBottom: '2px solid #dee2e6' }}>Channel Reference</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 1.5, px: 2, fontSize: '0.875rem', color: '#495057', borderBottom: '2px solid #dee2e6' }}>Marketplace</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 1.5, px: 2, fontSize: '0.875rem', color: '#495057', borderBottom: '2px solid #dee2e6' }}>Product</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 1.5, px: 2, fontSize: '0.875rem', color: '#495057', borderBottom: '2px solid #dee2e6' }}>Amount</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 1.5, px: 2, fontSize: '0.875rem', color: '#495057', borderBottom: '2px solid #dee2e6' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 1.5, px: 2, fontSize: '0.875rem', color: '#495057', borderBottom: '2px solid #dee2e6' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {returns.filter(item => !isAmazonReturn(item)).map((returnItem, index) => (
                  <React.Fragment key={returnItem.id}>
                    <TableRow 
                      hover 
                      sx={{ 
                        '&:hover': { bgcolor: '#f8f9fa' },
                        borderBottom: '1px solid #e9ecef',
                        bgcolor: index % 2 === 0 ? '#ffffff' : '#fafbfc'
                      }}
                    >
                      <TableCell sx={{ py: 1.5, px: 2 }}>
                        <IconButton
                          size="small"
                          onClick={() => toggleSellercloudExpansion(returnItem.id)}
                          sx={{ color: '#0066cc' }}
                        >
                          {expandedSellercloudRows.has(returnItem.id) ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                        </IconButton>
                      </TableCell>
                      
                      <TableCell sx={{ py: 1.5, px: 2, fontSize: '0.875rem', fontWeight: 500, color: '#495057' }}>
                        {returnItem.return_number.replace('RMA-', '')}
                      </TableCell>
                      
                      <TableCell sx={{ py: 1.5, px: 2 }}>
                        <Box>
                          <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 500, color: '#212529', mb: 0.5 }}>
                            {returnItem.customer_name}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#6c757d', fontSize: '0.75rem' }}>
                            {returnItem.customer_email && returnItem.customer_email.length > 30 ? returnItem.customer_email.substring(0, 30) + '...' : returnItem.customer_email}
                          </Typography>
                          <Box sx={{ mt: 0.5 }}>
                            <Chip 
                              label="Sellercloud" 
                              size="small" 
                              sx={{ 
                                height: '18px', 
                                fontSize: '0.65rem',
                                bgcolor: '#e7f3ff',
                                color: '#0066cc',
                                fontWeight: 500
                              }} 
                            />
                          </Box>
                        </Box>
                      </TableCell>
                      
                      <TableCell sx={{ py: 1.5, px: 2, fontSize: '0.875rem', color: '#495057' }}>
                        {returnItem.order_id}
                      </TableCell>
                      
                      <TableCell sx={{ py: 1.5, px: 2, fontSize: '0.875rem', color: '#495057' }}>
                        {returnItem.channel_order_ref && returnItem.channel_order_ref !== returnItem.order_id ? returnItem.channel_order_ref : 'N/A'}
                      </TableCell>
                      
                      <TableCell sx={{ py: 1.5, px: 2, fontSize: '0.875rem', color: '#495057' }}>
                        <Chip 
                          label={getChannelDisplayName(returnItem.order_source)} 
                          size="small" 
                          sx={{ 
                            height: '18px', 
                            fontSize: '0.65rem',
                            bgcolor: returnItem.order_source === 'eBay' || returnItem.order_source === 'ebay' ? '#fff2e6' : 
                                    returnItem.order_source === 'Amazon' || returnItem.order_source === 'amazon' ? '#fff3cd' :
                                    returnItem.order_source === 'Website' || returnItem.order_source === 'website' ? '#e8f5e8' : '#f0f0f0',
                            color: returnItem.order_source === 'eBay' || returnItem.order_source === 'ebay' ? '#cc6600' : 
                                   returnItem.order_source === 'Amazon' || returnItem.order_source === 'amazon' ? '#856404' :
                                   returnItem.order_source === 'Website' || returnItem.order_source === 'website' ? '#2e7d32' : '#666666',
                            fontWeight: 500
                          }} 
                        />
                      </TableCell>
                      
                      <TableCell sx={{ py: 1.5, px: 2, fontSize: '0.875rem', color: '#495057', maxWidth: '200px' }}>
                        <Box>
                          <Typography variant="body2" sx={{ 
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            mb: 0.25
                          }}>
                            {(returnItem.items && returnItem.items[0]?.sku) || returnItem.sku || 'N/A'}
                          </Typography>
                          <Typography variant="caption" sx={{ 
                            color: '#6c757d', 
                            fontSize: '0.75rem',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            display: 'block'
                          }}>
                            {(returnItem.items && returnItem.items[0]?.product_name) || returnItem.product_name || 'N/A'}
                          </Typography>
                        </Box>
                      </TableCell>
                      
                      <TableCell sx={{ py: 1.5, px: 2, fontSize: '0.875rem', fontWeight: 600, color: '#212529' }}>
                        {formatUKCurrency(returnItem.refund_amount)}
                      </TableCell>
                      
                      <TableCell sx={{ py: 1.5, px: 2 }}>
                        <Chip
                          label={returnItem.return_status}
                          size="small"
                          sx={{
                            fontSize: '0.75rem',
                            height: '22px',
                            fontWeight: 500,
                            textTransform: 'lowercase',
                            ...getStatusColor(returnItem.return_status)
                          }}
                        />
                      </TableCell>
                      
                      <TableCell sx={{ py: 1.5, px: 2 }}>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          {returnItem.return_status === 'pending' && (
                            <Button
                              variant="contained"
                              size="small"
                              startIcon={<ReceiveIcon />}
                              onClick={() => handleReceiveReturn(returnItem)}
                              sx={{
                                bgcolor: '#28a745',
                                '&:hover': { bgcolor: '#218838' },
                                fontSize: '0.75rem',
                                py: 0.25,
                                px: 1,
                                minWidth: 'auto'
                              }}
                            >
                              Receive
                            </Button>
                          )}
                          <IconButton 
                            size="small"
                            onClick={() => navigate(`/returns/${returnItem.id}`)}
                            sx={{ color: '#6c757d' }}
                          >
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                    
                    {/* Expandable Sellercloud Details Row */}
                    <TableRow>
                      <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={10}>
                        {renderSellercloudDetails(returnItem)}
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          </>
        )}

        {/* Tab Content - Amazon Returns */}
        {activeTab === 1 && (
          <TableContainer sx={{ px: 0, width: '100%', height: 'calc(100vh - 280px)', overflow: 'auto' }}>
            <Table stickyHeader size="small" sx={{ width: '100%' }}>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                  <TableCell sx={{ fontWeight: 600, py: 1.5, px: 2, fontSize: '0.875rem', color: '#495057', borderBottom: '2px solid #dee2e6' }}>Return Number</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 1.5, px: 2, fontSize: '0.875rem', color: '#495057', borderBottom: '2px solid #dee2e6' }}>Order Number</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 1.5, px: 2, fontSize: '0.875rem', color: '#495057', borderBottom: '2px solid #dee2e6' }}>Return Date</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 1.5, px: 2, fontSize: '0.875rem', color: '#495057', borderBottom: '2px solid #dee2e6' }}>Tracking Number</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 1.5, px: 2, fontSize: '0.875rem', color: '#495057', borderBottom: '2px solid #dee2e6' }}>Return Reason</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 1.5, px: 2, fontSize: '0.875rem', color: '#495057', borderBottom: '2px solid #dee2e6' }}>Item SKU</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 1.5, px: 2, fontSize: '0.875rem', color: '#495057', borderBottom: '2px solid #dee2e6' }}>Product</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 1.5, px: 2, fontSize: '0.875rem', color: '#495057', borderBottom: '2px solid #dee2e6' }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {amazonDisplayReturns.map((returnItem, index) => (
                  <React.Fragment key={returnItem.id}>
                    <TableRow 
                      hover 
                      sx={{ 
                        '&:hover': { bgcolor: '#f8f9fa' },
                        borderBottom: '1px solid #e9ecef',
                        bgcolor: index % 2 === 0 ? '#ffffff' : '#fafbfc'
                      }}
                    >
                      <TableCell sx={{ py: 1.5, px: 2, fontSize: '0.875rem', fontWeight: 500, color: '#495057' }}>
                        {returnItem.amazon_rma_id || returnItem.return_number || `RMA-${returnItem.id}`}
                      </TableCell>
                      
                      <TableCell sx={{ py: 1.5, px: 2, fontSize: '0.875rem', color: '#495057' }}>
                        {returnItem.order_id || 'N/A'}
                      </TableCell>
                      
                      <TableCell sx={{ py: 1.5, px: 2, fontSize: '0.875rem', color: '#495057' }}>
                        {formatUKDate(returnItem.return_request_date || returnItem.uploaded_at || returnItem.created_at)}
                      </TableCell>
                      
                      <TableCell sx={{ py: 1.5, px: 2, fontSize: '0.875rem', color: '#495057' }}>
                        {returnItem.tracking_number || 'N/A'}
                      </TableCell>
                      
                      <TableCell sx={{ py: 1.5, px: 2, fontSize: '0.875rem', color: '#495057', maxWidth: '200px' }}>
                        <Typography variant="body2" sx={{ 
                          fontSize: '0.875rem',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {returnItem.return_reason || returnItem.returnReasonCode || 'N/A'}
                        </Typography>
                      </TableCell>
                      
                      <TableCell sx={{ py: 1.5, px: 2, fontSize: '0.875rem', color: '#495057' }}>
                        {returnItem.sku || returnItem.amazon_sku || 'N/A'}
                      </TableCell>
                      
                      <TableCell sx={{ py: 1.5, px: 2, fontSize: '0.875rem', color: '#495057', maxWidth: '250px' }}>
                        <Typography variant="body2" sx={{ 
                          fontSize: '0.875rem',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {returnItem.product_name || returnItem.product_title || 'N/A'}
                        </Typography>
                      </TableCell>
                      
                      <TableCell sx={{ py: 1.5, px: 2 }}>
                        <Chip
                          label={returnItem.return_status || returnItem.status || 'approved'}
                          size="small"
                          sx={{
                            fontSize: '0.75rem',
                            height: '22px',
                            fontWeight: 500,
                            textTransform: 'lowercase',
                            bgcolor: '#e8f5e8',
                            color: '#2e7d32'
                          }}
                        />
                      </TableCell>
                    </TableRow>
                    
                    {/* Expandable Amazon Details Row */}
                    <TableRow>
                      <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
                        <Collapse in={expandedAmazonRows.has(returnItem.id)} timeout="auto" unmountOnExit>
                          <Box sx={{ p: 3, bgcolor: '#fff8f0', borderTop: '1px solid #FFE082' }}>
                            <Typography variant="h6" sx={{ mb: 2, color: '#FF9900', fontWeight: 600 }}>
                              📦 Amazon Return Details
                            </Typography>
                            
                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 2 }}>
                              {/* Product Info */}
                              <Paper sx={{ p: 2, bgcolor: 'white', border: '1px solid #FFE082' }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#E65100' }}>Product Information</Typography>
                                <Typography variant="body2" sx={{ mb: 0.5 }}><strong>ASIN:</strong> {returnItem.asin || 'N/A'}</Typography>
                                <Typography variant="body2" sx={{ mb: 0.5 }}><strong>SKU:</strong> {returnItem.sku || 'N/A'}</Typography>
                                <Typography variant="body2" sx={{ mb: 0.5 }}><strong>Product:</strong> {returnItem.product_name || 'N/A'}</Typography>
                                <Typography variant="body2" sx={{ mb: 0.5 }}><strong>Quantity:</strong> {returnItem.return_quantity || returnItem.quantity || '1'}</Typography>
                                <Typography variant="body2"><strong>Category:</strong> {returnItem.category || 'N/A'}</Typography>
                              </Paper>

                              {/* Order Information */}
                              <Paper sx={{ p: 2, bgcolor: 'white', border: '1px solid #FFE082' }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#1976D2' }}>Order Information</Typography>
                                <Typography variant="body2" sx={{ mb: 0.5 }}><strong>Order ID:</strong> {returnItem.order_id || 'N/A'}</Typography>
                                <Typography variant="body2" sx={{ mb: 0.5 }}><strong>Order Date:</strong> {formatUKDate(returnItem.orderDate || returnItem.return_request_date)}</Typography>
                                <Typography variant="body2" sx={{ mb: 0.5 }}><strong>Order Amount:</strong> £{returnItem.orderAmount || returnItem.order_total || '0.00'}</Typography>
                                <Typography variant="body2" sx={{ mb: 0.5 }}><strong>Prime Order:</strong> {returnItem.isPrime === 'Y' || returnItem.is_prime ? 'Yes' : 'No'}</Typography>
                                <Typography variant="body2"><strong>A-to-Z Claim:</strong> {returnItem.aToZClaim === 'true' || returnItem.a_to_z_claim ? 'Yes' : 'No'}</Typography>
                              </Paper>

                              {/* Return & Shipping Info */}
                              <Paper sx={{ p: 2, bgcolor: 'white', border: '1px solid #FFE082' }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#2E7D32' }}>Return & Shipping</Typography>
                                <Typography variant="body2" sx={{ mb: 0.5 }}><strong>Return Date:</strong> {formatUKDate(returnItem.return_request_date)}</Typography>
                                <Typography variant="body2" sx={{ mb: 0.5 }}><strong>Tracking ID:</strong> {returnItem.tracking_number || 'N/A'}</Typography>
                                <Typography variant="body2" sx={{ mb: 0.5 }}><strong>Carrier:</strong> {returnItem.returnCarrier || returnItem.carrier || 'N/A'}</Typography>
                                <Typography variant="body2" sx={{ mb: 0.5 }}><strong>Return Type:</strong> {returnItem.returnType || returnItem.return_type || 'N/A'}</Typography>
                                <Typography variant="body2"><strong>In Policy:</strong> {returnItem.inPolicy === 'Y' || returnItem.in_policy ? 'Yes' : 'No'}</Typography>
                              </Paper>

                              {/* Financial Information */}
                              <Paper sx={{ p: 2, bgcolor: 'white', border: '1px solid #FFE082' }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#9C27B0' }}>Financial Details</Typography>
                                <Typography variant="body2" sx={{ mb: 0.5 }}><strong>Refund Amount:</strong> {formatUKCurrency(returnItem.refundAmount || returnItem.refund_amount)}</Typography>
                                <Typography variant="body2" sx={{ mb: 0.5 }}><strong>Label Cost:</strong> {formatUKCurrency(returnItem.label_cost)}</Typography>
                                <Typography variant="body2" sx={{ mb: 0.5 }}><strong>Label Paid By:</strong> {returnItem.labelToBePaidBy || returnItem.label_paid_by || 'Customer'}</Typography>
                                <Typography variant="body2" sx={{ mb: 0.5 }}><strong>Resolution:</strong> {returnItem.resolution || 'Pending'}</Typography>
                                <Typography variant="body2"><strong>Status:</strong> {returnItem.return_status || returnItem.status || 'Processing'}</Typography>
                              </Paper>
                            </Box>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Tab Content - eBay Returns */}
        {activeTab === 2 && (
          <TableContainer sx={{ px: 0, width: '100%', height: 'calc(100vh - 280px)', overflow: 'auto' }}>
            <Table stickyHeader size="small" sx={{ width: '100%' }}>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                  <TableCell sx={{ fontWeight: 600, color: '#495057', fontSize: '0.875rem', whiteSpace: 'nowrap', width: '50px' }}></TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#495057', fontSize: '0.875rem', whiteSpace: 'nowrap', minWidth: '120px' }}>Return ID</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#495057', fontSize: '0.875rem', whiteSpace: 'nowrap', minWidth: '120px' }}>eBay Order ID</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#495057', fontSize: '0.875rem', whiteSpace: 'nowrap', minWidth: '150px' }}>Customer</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#495057', fontSize: '0.875rem', whiteSpace: 'nowrap', minWidth: '100px' }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#495057', fontSize: '0.875rem', whiteSpace: 'nowrap', minWidth: '80px' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#495057', fontSize: '0.875rem', whiteSpace: 'nowrap', minWidth: '100px' }}>Reason</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#495057', fontSize: '0.875rem', whiteSpace: 'nowrap', minWidth: '100px' }}>Product</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#495057', fontSize: '0.875rem', whiteSpace: 'nowrap', minWidth: '80px' }}>Value</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#495057', fontSize: '0.875rem', whiteSpace: 'nowrap', width: '100px' }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ebayDisplayReturns.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body2" sx={{ color: '#6c757d', fontStyle: 'italic' }}>
                        No eBay returns found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  ebayDisplayReturns.map((returnItem, index) => (
                    <React.Fragment key={returnItem.ebay_return_id || index}>
                      <TableRow 
                        sx={{ 
                          '&:hover': { bgcolor: '#f8f9fa' },
                          borderLeft: '4px solid #0066cc',
                          bgcolor: '#fff5f0'
                        }}
                      >
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => toggleEbayExpansion(returnItem.ebay_return_id || index)}
                          >
                            {expandedEbayRows.has(returnItem.ebay_return_id || index) ? 
                              <KeyboardArrowUp /> : <KeyboardArrowDown />}
                          </IconButton>
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.8rem', color: '#0066cc', fontWeight: 500 }}>
                          {returnItem.return_number || returnItem.ebay_return_id || 'N/A'}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.8rem' }}>
                          {returnItem.ebay_order_id || returnItem.order_id || 'N/A'}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.8rem' }}>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {returnItem.customer_name || returnItem.ebay_buyer_username || 'eBay Buyer'}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#6c757d' }}>
                              {returnItem.customer_email || returnItem.ebay_buyer_username || 'N/A'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.8rem' }}>
                          {returnItem.formatted_return_date || new Date(returnItem.return_date).toLocaleDateString('en-GB')}
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={returnItem.return_status || returnItem.ebay_status || 'PENDING'} 
                            size="small"
                            sx={{ 
                              bgcolor: returnItem.return_status === 'COMPLETED' ? '#d4edda' : '#fff3cd',
                              color: returnItem.return_status === 'COMPLETED' ? '#155724' : '#856404',
                              fontSize: '0.7rem',
                              fontWeight: 500
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.8rem' }}>
                          {returnItem.return_reason || 'Not specified'}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.8rem', maxWidth: '150px' }}>
                          <Typography variant="body2" sx={{ 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis', 
                            whiteSpace: 'nowrap' 
                          }}>
                            {returnItem.product_name || 'eBay Item'}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#6c757d' }}>
                            {returnItem.sku || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.8rem', fontWeight: 500 }}>
                          {formatUKCurrency(returnItem.return_value || 0)} {returnItem.currency || 'GBP'}
                        </TableCell>
                        <TableCell>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => navigate(`/returns/${returnItem.ebay_return_id || returnItem.return_number}`)}
                            sx={{ 
                              minWidth: 'auto',
                              px: 2,
                              py: 0.5,
                              fontSize: '0.7rem',
                              bgcolor: '#0066cc',
                              '&:hover': { bgcolor: '#0056b3' }
                            }}
                          >
                            Select
                          </Button>
                        </TableCell>
                      </TableRow>
                      
                      {/* Expandable Content */}
                      <TableRow>
                        <TableCell 
                          colSpan={10} 
                          sx={{ py: 0, border: expandedEbayRows.has(returnItem.ebay_return_id || index) ? '1px solid #e9ecef' : 'none' }}
                        >
                          <Collapse 
                            in={expandedEbayRows.has(returnItem.ebay_return_id || index)} 
                            timeout="auto" 
                            unmountOnExit
                          >
                            <Box sx={{ p: 2, bgcolor: '#f8f9fa' }}>
                              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#495057' }}>
                                eBay Return Details - {returnItem.return_number || returnItem.ebay_return_id} 
                                {returnItem.order_id && ` (SC-${returnItem.order_id})`}
                              </Typography>
                              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 2 }}>
                                
                                {/* Return Information */}
                                <Paper sx={{ p: 2, bgcolor: 'white', border: '1px solid #dee2e6' }}>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#495057' }}>
                                    📦 Return Information
                                  </Typography>
                                  <Typography variant="body2" sx={{ mb: 0.5 }}><strong>eBay Return ID:</strong> {returnItem.ebay_return_id || 'N/A'}</Typography>
                                  <Typography variant="body2" sx={{ mb: 0.5 }}><strong>Return Type:</strong> {returnItem.ebay_return_type || 'RETURN'}</Typography>
                                  <Typography variant="body2" sx={{ mb: 0.5 }}><strong>Return Age:</strong> {returnItem.return_age_days || 0} days</Typography>
                                  <Typography variant="body2" sx={{ mb: 0.5 }}><strong>eBay Order ID:</strong> {returnItem.ebay_order_id || 'N/A'}</Typography>
                                  <Typography variant="body2" sx={{ mb: 0.5 }}><strong>Quantity:</strong> {returnItem.quantity || 1}</Typography>
                                  <Typography variant="body2" sx={{ mb: 0.5 }}><strong>Received On:</strong> {returnItem.received_on ? new Date(returnItem.received_on).toLocaleString('en-GB') : 'Not received'}</Typography>
                                  <Typography variant="body2"><strong>Notes:</strong> {returnItem.notes || 'None'}</Typography>
                                </Paper>

                                {/* Comprehensive Tracking & Shipment Information */}
                                <Paper sx={{ p: 2, bgcolor: 'white', border: '1px solid #28a745' }}>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#28a745' }}>
                                    🚚 Tracking & Shipment Details
                                    {returnItem.hasTracking && (
                                      <Chip 
                                        label="Live Tracking Available" 
                                        size="small" 
                                        color="success" 
                                        sx={{ ml: 1, fontSize: '0.6rem' }}
                                      />
                                    )}
                                  </Typography>
                                  
                                  {/* Primary Tracking Information */}
                                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                                    <strong>Tracking Number:</strong> {returnItem.tracking_number || 'Not provided'}
                                  </Typography>
                                  
                                  {/* Enhanced Tracking Summary */}
                                  {returnItem.trackingSummary && (
                                    <Box sx={{ mb: 1, p: 1, bgcolor: '#f0f8f0', borderRadius: 1, border: '1px solid #d4edda' }}>
                                      <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600, color: '#155724' }}>
                                        📦 Live Tracking Status
                                      </Typography>
                                      <Typography variant="body2" sx={{ mb: 0.3 }}>
                                        <strong>Carrier:</strong> {returnItem.trackingSummary.carrier || 'N/A'}
                                      </Typography>
                                      <Typography variant="body2" sx={{ mb: 0.3 }}>
                                        <strong>Tracking Number:</strong> {returnItem.trackingSummary.trackingNumber || 'N/A'}
                                      </Typography>
                                      <Typography variant="body2" sx={{ mb: 0.3 }}>
                                        <strong>Status:</strong> {returnItem.trackingSummary.status || 'Unknown'}
                                      </Typography>
                                      {returnItem.trackingSummary.lastUpdate && (
                                        <Typography variant="body2" sx={{ mb: 0.3 }}>
                                          <strong>Last Update:</strong> {new Date(returnItem.trackingSummary.lastUpdate).toLocaleString('en-GB')}
                                        </Typography>
                                      )}
                                      {returnItem.trackingSummary.estimatedDelivery && (
                                        <Typography variant="body2" sx={{ mb: 0.3 }}>
                                          <strong>Expected Delivery:</strong> {new Date(returnItem.trackingSummary.estimatedDelivery).toLocaleDateString('en-GB')}
                                        </Typography>
                                      )}
                                    </Box>
                                  )}
                                  
                                  {/* Scan History */}
                                  {returnItem.trackingSummary?.scanHistory && returnItem.trackingSummary.scanHistory.length > 0 && (
                                    <Box sx={{ mb: 1 }}>
                                      <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600 }}>
                                        📍 Scan History:
                                      </Typography>
                                      <Box sx={{ maxHeight: 120, overflowY: 'auto', p: 1, bgcolor: '#f8f9fa', borderRadius: 1 }}>
                                        {returnItem.trackingSummary.scanHistory.slice(0, 5).map((scan, index) => (
                                          <Typography key={index} variant="body2" sx={{ fontSize: '0.75rem', mb: 0.5 }}>
                                            <strong>{scan.eventTime ? new Date(scan.eventTime.value).toLocaleString('en-GB') : 'N/A'}:</strong> {scan.eventDesc || scan.eventStatus} 
                                            {scan.eventCity && ` - ${scan.eventCity}`}
                                            {scan.eventStateOrProvince && `, ${scan.eventStateOrProvince}`}
                                            {scan.eventPostalCode && ` ${scan.eventPostalCode}`}
                                          </Typography>
                                        ))}
                                      </Box>
                                    </Box>
                                  )}
                                  
                                  {/* Standard Return Information */}
                                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                                    <strong>Warehouse ID:</strong> {returnItem.warehouse_id || 'N/A'}
                                  </Typography>
                                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                                    <strong>Received Status:</strong> {returnItem.received_status || 'Not received'}
                                  </Typography>
                                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                                    <strong>Refund Status:</strong> {returnItem.refund_status || 'Pending'}
                                  </Typography>
                                  
                                  {/* Additional Tracking Numbers */}
                                  {returnItem.raw_ebay_data?.TrackingNumber && returnItem.raw_ebay_data.TrackingNumber !== returnItem.tracking_number && (
                                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                                      <strong>eBay Tracking:</strong> {returnItem.raw_ebay_data.TrackingNumber}
                                    </Typography>
                                  )}
                                  
                                  {/* Multiple Tracking Entries */}
                                  {returnItem.tracking && returnItem.tracking.length > 1 && (
                                    <Box sx={{ mt: 1, p: 1, bgcolor: '#fff3cd', borderRadius: 1 }}>
                                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                        📦 Multiple Tracking Records:
                                      </Typography>
                                      {returnItem.tracking.map((track, index) => (
                                        <Typography key={index} variant="body2" sx={{ fontSize: '0.75rem', mb: 0.3 }}>
                                          {index + 1}. {track.carrier || 'Unknown Carrier'} - {track.trackingNumber || 'No tracking number'} 
                                          ({track.status || 'Unknown status'})
                                        </Typography>
                                      ))}
                                    </Box>
                                  )}
                                </Paper>

                                {/* Financial Information */}
                                <Paper sx={{ p: 2, bgcolor: 'white', border: '1px solid #0066cc' }}>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#0066cc' }}>
                                    💰 Financial Details
                                  </Typography>
                                  <Typography variant="body2" sx={{ mb: 0.5 }}><strong>Return Value:</strong> {formatUKCurrency(returnItem.return_value || 0)} {returnItem.currency || 'GBP'}</Typography>
                                  <Typography variant="body2" sx={{ mb: 0.5 }}><strong>Return Status:</strong> {returnItem.return_status || 'Pending'}</Typography>
                                  <Typography variant="body2" sx={{ mb: 0.5 }}><strong>eBay Status:</strong> {returnItem.ebay_status || 'N/A'}</Typography>
                                  <Typography variant="body2"><strong>Refund Status:</strong> {returnItem.refund_status || 'Pending'}</Typography>
                                </Paper>

                                {/* Item Information */}
                                <Paper sx={{ p: 2, bgcolor: 'white', border: '1px solid #6f42c1' }}>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#6f42c1' }}>
                                    🛍️ Item Details
                                  </Typography>
                                  <Typography variant="body2" sx={{ mb: 0.5 }}><strong>eBay Item ID:</strong> {returnItem.ebay_item_id || 'N/A'}</Typography>
                                  <Typography variant="body2" sx={{ mb: 0.5 }}><strong>Product:</strong> {returnItem.product_name || returnItem.ebay_listing_title || 'N/A'}</Typography>
                                  <Typography variant="body2" sx={{ mb: 0.5 }}><strong>SKU:</strong> {returnItem.sku || 'N/A'}</Typography>
                                  <Typography variant="body2"><strong>Return Reason:</strong> {returnItem.return_reason || 'Not specified'}</Typography>
                                </Paper>
                              </Box>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Pagination removed for full screen view */}
      </Paper>

      {/* Amazon Upload Dialog */}
      <Dialog 
        open={uploadDialogOpen} 
        onClose={() => setUploadDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Upload Amazon Returns XML File</DialogTitle>
        <DialogContent>
          <AmazonUpload 
            onUploadSuccess={() => {
              setUploadDialogOpen(false);
              fetchReturns('amazon', 0, 100, search, statusFilter, true); // Refresh Amazon data
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* New Return Dialog */}
      <Dialog 
        open={newReturnDialogOpen} 
        onClose={() => {
          setNewReturnDialogOpen(false);
          setNewReturnType('');
          setQuickReturnData({
            orderId: '',
            platform: 'sellercloud',
            customerEmail: '',
            customerName: '',
            reason: '',
            notes: ''
          });
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: '#28a745', color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
          <AddIcon />
          Create New Return
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {!newReturnType ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
              <Typography variant="h6" sx={{ mb: 2, color: '#495057' }}>
                Choose how you'd like to create a new return:
              </Typography>
              
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
                <Card 
                  sx={{ 
                    p: 3, 
                    cursor: 'pointer', 
                    border: '2px solid transparent',
                    '&:hover': { 
                      border: '2px solid #28a745',
                      boxShadow: '0 4px 12px rgba(40, 167, 69, 0.2)'
                    }
                  }}
                  onClick={() => handleNewReturnTypeSelect('quick')}
                >
                  <Typography variant="h6" sx={{ color: '#28a745', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AddIcon />
                    Quick Start
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#6c757d', mb: 2 }}>
                    Start with basic order information and complete the full return creation process.
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#495057', fontWeight: 500 }}>
                    • Enter Order ID and basic details
                    • Continues to full creation workflow
                    • Best for most return scenarios
                  </Typography>
                </Card>

                <Card 
                  sx={{ 
                    p: 3, 
                    cursor: 'pointer', 
                    border: '2px solid transparent',
                    '&:hover': { 
                      border: '2px solid #007bff',
                      boxShadow: '0 4px 12px rgba(0, 123, 255, 0.2)'
                    }
                  }}
                  onClick={() => handleNewReturnTypeSelect('full')}
                >
                  <Typography variant="h6" sx={{ color: '#007bff', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckIcon />
                    Full Creation Wizard
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#6c757d', mb: 2 }}>
                    Complete step-by-step return creation with order search and item selection.
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#495057', fontWeight: 500 }}>
                    • Order search and verification
                    • Item selection and details
                    • Complete workflow in new page
                  </Typography>
                </Card>
              </Box>
            </Box>
          ) : newReturnType === 'quick' ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
              <Typography variant="h6" sx={{ mb: 2, color: '#495057' }}>
                Quick Return Setup
              </Typography>
              
              <TextField
                fullWidth
                label="Order ID"
                placeholder="Enter order ID (e.g., 206-1234567-8901234 or SC123456)"
                value={quickReturnData.orderId}
                onChange={(e) => setQuickReturnData({...quickReturnData, orderId: e.target.value})}
                required
              />
              
              <FormControl fullWidth>
                <InputLabel>Platform</InputLabel>
                <Select
                  value={quickReturnData.platform}
                  onChange={(e) => setQuickReturnData({...quickReturnData, platform: e.target.value})}
                  label="Platform"
                >
                  <MenuItem value="sellercloud">Sellercloud</MenuItem>
                  <MenuItem value="amazon">Amazon</MenuItem>
                  <MenuItem value="ebay">eBay</MenuItem>
                </Select>
              </FormControl>
              
              <TextField
                fullWidth
                label="Customer Email"
                type="email"
                placeholder="customer@example.com"
                value={quickReturnData.customerEmail}
                onChange={(e) => setQuickReturnData({...quickReturnData, customerEmail: e.target.value})}
              />
              
              <TextField
                fullWidth
                label="Customer Name"
                placeholder="John Doe"
                value={quickReturnData.customerName}
                onChange={(e) => setQuickReturnData({...quickReturnData, customerName: e.target.value})}
              />
              
              <TextField
                fullWidth
                label="Return Reason"
                placeholder="Product defective, wrong item, etc."
                value={quickReturnData.reason}
                onChange={(e) => setQuickReturnData({...quickReturnData, reason: e.target.value})}
              />
              
              <TextField
                fullWidth
                label="Additional Notes"
                multiline
                rows={3}
                placeholder="Any additional information about this return..."
                value={quickReturnData.notes}
                onChange={(e) => setQuickReturnData({...quickReturnData, notes: e.target.value})}
              />
            </Box>
          ) : null}
        </DialogContent>
        
        <DialogActions sx={{ p: 3, borderTop: '1px solid #dee2e6' }}>
          <Button 
            onClick={() => {
              setNewReturnDialogOpen(false);
              setNewReturnType('');
            }}
          >
            Cancel
          </Button>
          
          {newReturnType === 'quick' && (
            <Button 
              variant="contained"
              onClick={handleQuickReturnSubmit}
              disabled={!quickReturnData.orderId || newReturnLoading}
              sx={{ bgcolor: '#28a745', '&:hover': { bgcolor: '#218838' } }}
            >
              {newReturnLoading ? 'Loading...' : 'Continue to Full Creation'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* QA Check Dialog */}
      <Dialog open={qaDialog} onClose={() => setQaDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ReceiveIcon color="primary" />
            QA Check - Mark as Received
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
            {selectedReturnItem ? `RMA: ${selectedReturnItem.return_number}` : ''}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Complete the QA check for this return and add any notes about the condition of returned items.
            These notes will be available to the CS Team for the refund/exchange process and will be synced to Sellercloud.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="QA Check Notes"
            placeholder="Enter condition notes, damages, or other observations about returned items..."
            value={qaCheckNotes}
            onChange={(e) => setQaCheckNotes(e.target.value)}
            variant="outlined"
            sx={{ mt: 1 }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Note: If no issues are found, you can leave this blank and a default "no issues" note will be added.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQaDialog(false)} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={handleQACheckComplete} 
            variant="contained" 
            startIcon={<CheckIcon />}
            disabled={isUpdating}
            sx={{ bgcolor: '#28a745', '&:hover': { bgcolor: '#218838' } }}
          >
            {isUpdating ? 'Processing...' : 'Complete QA Check & Mark Received'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReturnsTable;
